import { useCallback } from 'react'
import { graphql, useMutation, useRefetchableFragment } from 'react-relay'

import {
  type useAppealPhotos_profile$data,
  type useAppealPhotos_profile$key
} from '@/__generated__/useAppealPhotos_profile.graphql'
import { type useAppealPhotosCreatePhotoMutation } from '@/__generated__/useAppealPhotosCreatePhotoMutation.graphql'
import { type UseAppealPhotosQuery } from '@/__generated__/UseAppealPhotosQuery.graphql'

export type UseAppealPhotosEdge = NonEmpty<
  ElementOf<NonEmpty<NonEmpty<useAppealPhotos_profile$data['user']>['appealPhotos']['edges']>>
>

export const useAppealPhotos = (profileRef: useAppealPhotos_profile$key | null | undefined) => {
  // noinspection GraphQLUnresolvedReference
  const [profileData, refetchAppealPhotos] = useRefetchableFragment<UseAppealPhotosQuery, useAppealPhotos_profile$key>(
    graphql`
      fragment useAppealPhotos_profile on Profile
      @argumentDefinitions(after: { type: "Cursor" }, first: { type: "Int", defaultValue: 10 })
      @refetchable(queryName: "UseAppealPhotosQuery") {
        user {
          appealPhotos(after: $after, first: $first) @connection(key: "useAppealPhotos_profile_appealPhotos") {
            __id
            edges {
              cursor
              node {
                description
                rowId
                sequence
                storageObject {
                  blurhash
                  bucket
                  key
                }
              }
            }
          }
        }
      }
    `,
    profileRef
  )
  const [_createAppealPhoto, isCreatingAppealPhoto] = useMutation<useAppealPhotosCreatePhotoMutation>(graphql`
    mutation useAppealPhotosCreatePhotoMutation($connections: [ID!]!, $input: CreatePhotoInput!) {
      createPhoto(input: $input) {
        photoEdge(orderBy: [SEQUENCE_ASC]) @appendEdge(connections: $connections) {
          cursor
          node {
            description
            id
            rowId
            sequence
            storageObject {
              bucket
              key
            }
          }
        }
      }
    }
  `)
  const createAppealPhoto = useCallback(
    (storageObjectId: string): Promise<useAppealPhotosCreatePhotoMutation['response']> =>
      new Promise((resolve, reject) =>
        _createAppealPhoto({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors[0])
            } else {
              resolve(response)
            }
          },
          onError: (error) => reject(error),
          variables: {
            connections: profileData?.user?.appealPhotos?.__id ? [profileData?.user?.appealPhotos?.__id] : [],
            input: {
              photo: {
                storageObjectId,
                type: 'APPEAL'
              }
            }
          }
        })
      ),
    [_createAppealPhoto, profileData?.user?.appealPhotos?.__id]
  )

  return {
    createAppealPhoto,
    isCreatingAppealPhoto,
    appealPhotosConnection: profileData?.user?.appealPhotos,
    refetchAppealPhotos
  }
}
